import { AnimatePresence, motion } from "motion/react";
import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";

/* One stack for every overlay (modal, drawer, palette, confirm) so Escape closes only the top
   one, Tab stays inside it, body scroll is locked once and focus returns to the opener. */
const stack: symbol[] = [];
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const isOverlayOpen = (): boolean => stack.length > 0;

export function useOverlay<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const me = Symbol("overlay");
    stack.push(me);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      // A late timer must never pull focus back from a field the person already moved to.
      if (ref.current && document.activeElement instanceof Node && ref.current.contains(document.activeElement) && document.activeElement !== ref.current) return;
      const el = ref.current?.querySelector<HTMLElement>("[data-autofocus]") ?? ref.current?.querySelector<HTMLElement>("input:not([type=hidden]), textarea, select") ?? ref.current;
      el?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== me) return;
      // A field that handles Escape itself (an inline "add" row) gets it instead of the dialog.
      if (e.key === "Escape" && e.target instanceof Element && e.target.closest("[data-local-escape]")) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key === "Tab" && ref.current) {
        const f = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((x) => !x.closest("[inert]") && x.offsetParent !== null);
        if (!f.length) {
          e.preventDefault();
          return;
        }
        const first = f[0]!;
        const last = f[f.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === ref.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey, true);
      const i = stack.indexOf(me);
      if (i !== -1) stack.splice(i, 1);
      if (!stack.length) document.body.style.overflow = prevOverflow;
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);
  return ref;
}

const spring = { type: "spring", stiffness: 460, damping: 36 } as const;
const Scrim = ({ onClick }: { onClick: () => void }) => (
  <motion.div className="absolute inset-0 bg-scrim backdrop-blur-[1px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} onClick={onClick} aria-hidden="true" />
);

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** px; the panel never exceeds the viewport. */
  width?: number;
  /** Blocks backdrop/Escape close while a submit is in flight. */
  dismissible?: boolean;
  role?: "dialog" | "alertdialog";
  className?: string;
  bodyClassName?: string;
  hideTitle?: boolean;
};

/** Fixed overlay neutral-900 at 40% with a 1 px blur, white 16 px panel, centred, max
 * height 90vh with internal scroll, close X top-right (§3.3). */
export function Modal({ open, onClose, title, subtitle, children, footer, width = 480, dismissible = true, role = "dialog", className, bodyClassName, hideTitle }: ModalProps) {
  const close = dismissible ? onClose : () => undefined;
  const ref = useOverlay<HTMLDivElement>(open, close);
  const titleId = useId();
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-3 sm:p-4">
          <Scrim onClick={close} />
          <motion.div
            ref={ref}
            role={role}
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className={cn("relative flex max-h-[90vh] w-full flex-col rounded-[16px] border border-line bg-raised shadow-[var(--shadow-raised)] outline-none", className)}
            style={{ maxWidth: width }}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 4, transition: { duration: 0.12 } }}
            transition={spring}
          >
            <div className={cn("flex items-start justify-between gap-3 px-6 pt-5", hideTitle ? "pb-0" : "pb-3")}>
              <div className={cn("min-w-0", hideTitle && "sr-only")}>
                <h2 id={titleId} className="text-lg font-bold text-neutral-900">
                  {title}
                </h2>
                {subtitle && <div className="mt-0.5 text-sm text-neutral-500">{subtitle}</div>}
              </div>
              <button type="button" className="btn btn-ghost btn-icon btn-sm -mr-2 -mt-1 ml-auto" onClick={close} aria-label="Close" disabled={!dismissible}>
                <X />
              </button>
            </div>
            <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 pb-5", bodyClassName)}>{children}</div>
            {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-100 px-6 py-4">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/** Right-hand panel (the Checkout customer picker, §3.3). */
export function Drawer({ open, onClose, title, children, footer, width = 400 }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; width?: number }) {
  const ref = useOverlay<HTMLDivElement>(open, onClose);
  const titleId = useId();
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <Scrim onClick={onClose} />
          <motion.aside
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="absolute inset-y-0 right-0 flex max-w-full flex-col bg-surface shadow-[-24px_0_60px_-20px_rgb(15_23_42/0.35)] outline-none"
            style={{ width }}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={spring}
          >
            <header className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4">
              <h2 id={titleId} className="card-title">
                {title}
              </h2>
              <button type="button" className="btn btn-ghost btn-icon btn-sm -mr-2" onClick={onClose} aria-label="Close">
                <X />
              </button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
            {footer && <footer className="border-t border-neutral-100 px-5 py-4">{footer}</footer>}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
